import React, { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import { api } from "@/lib/api";
import { miniInput } from "@/admin/SortableList";

const FLAGS: [string, string][] = [["limited_edition", "Marked Limited Edition in the lookbook"], ["atelier_edition", "Marked Atelier Edition in the lookbook"], ["in_collection", "Part of a lookbook collection"]];
const tid = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

function Chips({ label, value = [], options = [], onChange, testId }: any) {
  const [txt, setTxt] = useState("");
  const add = (v: string) => { v = v.trim(); if (v && !value.includes(v)) onChange([...value, v]); setTxt(""); };
  return (
    <div data-testid={testId}>
      <p className="text-xs text-neutral-500 mb-1.5">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {value.map((v: string) => (
          <button type="button" key={v} onClick={() => onChange(value.filter((x: string) => x !== v))} className="flex items-center gap-1 text-xs bg-neutral-900 text-white px-2 py-1" data-testid={`${testId}-chip-${tid(v)}`}>{v}<X size={11} /></button>
        ))}
        {options.filter((o: string) => !value.includes(o)).map((o: string) => (
          <button type="button" key={o} onClick={() => add(o)} className="text-xs border border-neutral-300 px-2 py-1 hover:border-neutral-900" data-testid={`${testId}-opt-${tid(o)}`}>+ {o}</button>
        ))}
        <input value={txt} onChange={(e) => setTxt(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(txt); } }} onBlur={() => txt && add(txt)}
          placeholder="Type & press Enter" className={miniInput + " w-44"} data-testid={`${testId}-input`} />
      </div>
    </div>
  );
}

function Picker({ label, ids = [], products, onChange, testId }: any) {
  const [q, setQ] = useState("");
  const byId = useMemo(() => Object.fromEntries(products.map((p: any) => [p.id, p])), [products]);
  const term = q.trim().toLowerCase();
  const hits = term.length < 2 ? [] : products.filter((p: any) => !ids.includes(p.id) && `${p.name} ${p.sku}`.toLowerCase().includes(term)).slice(0, 8);
  return (
    <div data-testid={testId}>
      <p className="text-xs text-neutral-500 mb-1.5">{label}</p>
      {ids.map((id: string) => (
        <div key={id} className="flex items-center gap-2 text-xs py-1" data-testid={`${testId}-item-${id}`}>
          {byId[id]?.image && <img src={byId[id].image} alt="" className="h-10 w-10 object-cover bg-white border border-neutral-200" data-testid={`${testId}-thumb-${id}`} />}
          <span className="flex-1">{byId[id]?.name || id}{byId[id] && !byId[id].published && <em className="text-neutral-400"> (not live)</em>}</span>
          <button type="button" onClick={() => onChange(ids.filter((x: string) => x !== id))} aria-label="Remove" data-testid={`${testId}-remove-${id}`}><X size={13} /></button>
        </div>
      ))}
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products by name or SKU" className={miniInput + " w-full mt-1"} data-testid={`${testId}-search`} />
      {hits.length > 0 && (
        <div className="border border-neutral-200 bg-white max-h-56 overflow-y-auto">
          {hits.map((p: any) => (
            <button type="button" key={p.id} onClick={() => { onChange([...ids, p.id]); setQ(""); }} className="flex w-full items-center gap-2 px-2 py-1.5 text-xs text-left hover:bg-neutral-50" data-testid={`${testId}-hit-${p.id}`}>
              <img src={p.image} alt="" className="h-8 w-8 object-cover" />{p.name} <span className="text-neutral-400">· {p.sku}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Preview({ rule }: any) {
  const [res, setRes] = useState<any>(null);
  const key = JSON.stringify(rule);
  useEffect(() => {
    const t = setTimeout(() => api.post("/admin-x/collections/preview", { rule }).then((r) => setRes(r.data)).catch(() => {}), 350);
    return () => clearTimeout(t);
  }, [key]);
  if (!res) return null;
  const empty = !Object.values(rule).some((v: any) => Array.isArray(v) && v.length);
  return (
    <div className="bg-[#F7F6F2] p-3" data-testid="rule-preview">
      {empty && <p className="text-xs text-amber-800 mb-1" data-testid="rule-preview-empty">No rules set yet: Edition collections will show the Coming soon message; other collections list every product.</p>}
      <p className="text-xs" data-testid="rule-preview-count"><strong>{res.total}</strong> live product{res.total === 1 ? "" : "s"} match{res.total === 1 ? "es" : ""}{res.total === 0 ? " — shoppers see the Coming soon message" : ""}</p>
      <div className="flex gap-1.5 mt-2 overflow-x-auto">{res.items.map((p: any) => <img key={p.id} src={p.image} alt={p.name} title={p.name} className="h-12 w-12 object-contain bg-white border border-neutral-200 shrink-0" />)}</div>
    </div>
  );
}

export function RuleEditor({ value, onChange }: { value: any; onChange: (v: any) => void }) {
  const rule = value && typeof value === "object" ? value : {};
  const set = (k: string, v: any) => onChange({ ...rule, [k]: v });
  const [opts, setOpts] = useState<any>({});
  const [products, setProducts] = useState<any[]>([]);
  useEffect(() => {
    api.get("/admin-x/collection-options").then((r) => setOpts(r.data)).catch(() => {});
    api.get("/admin-x/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);
  const flags: string[] = rule.flags || [];
  return (
    <div className="space-y-4 border border-neutral-200 p-4" data-testid="rule-editor">
      <p className="text-xs text-neutral-500">Products from the nightly lookbook sync join automatically when they match the <strong>product type</strong> AND <strong>any</strong> of the styles, tags, lookbook collections, edition marks or name words below. Hand-picked products always show; excluded ones never do.</p>
      <Chips label="Product types (blank = all)" value={rule.product_types} options={opts.product_types} onChange={(v: any) => set("product_types", v)} testId="rule-product-types" />
      <div className="space-y-1.5" data-testid="rule-flags">
        {FLAGS.map(([k, l]) => (
          <label key={k} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={flags.includes(k)} onChange={(e) => set("flags", e.target.checked ? [...flags, k] : flags.filter((f) => f !== k))} data-testid={`rule-flag-${k}`} />{l}</label>
        ))}
      </div>
      <Chips label="Lookbook tags" value={rule.tags} options={opts.tags} onChange={(v: any) => set("tags", v)} testId="rule-tags" />
      <Chips label="Styles" value={rule.styles} options={opts.styles} onChange={(v: any) => set("styles", v)} testId="rule-styles" />
      <Chips label="Lookbook collection names" value={rule.collection_names} options={opts.collection_names} onChange={(v: any) => set("collection_names", v)} testId="rule-collection-names" />
      <Chips label="Words in the product name" value={rule.keywords} onChange={(v: any) => set("keywords", v)} testId="rule-keywords" />
      <Picker label="Hand-picked products (always shown)" ids={rule.include_ids} products={products} onChange={(v: any) => set("include_ids", v)} testId="rule-include" />
      <Picker label="Excluded products (never shown)" ids={rule.exclude_ids} products={products} onChange={(v: any) => set("exclude_ids", v)} testId="rule-exclude" />
      <Preview rule={rule} />
    </div>
  );
}
